import { create } from 'zustand';
import type { SafeUser, Company } from '../types/auth';

interface AuthState {
  token: string | null;
  sessionId: string | null;
  tokenExpiresAt: number | null; // Unix ms
  user: SafeUser | null;
  company: Company | null;
  login: (token: string, user: SafeUser, company: Company | null) => void;
  logout: () => void;
}

function loadToken(): string | null {
  // Firmen-Benutzer: localStorage (überlebt Browser-Neustart, Dauer per Firmen-Konfig).
  // Admins: sessionStorage (überlebt F5 im selben Tab, stirbt mit dem Tab).
  try { return localStorage.getItem('eves_token') ?? sessionStorage.getItem('eves_token'); } catch { return null; }
}

interface JwtPayload { sessionId?: string; exp?: number; }

function decodeJwt(token: string): JwtPayload {
  try { return JSON.parse(atob(token.split('.')[1]!)) as JwtPayload; } catch { return {}; }
}

function decodeSessionId(token: string): string | null {
  return decodeJwt(token).sessionId ?? null;
}

function decodeExpiresAt(token: string): number | null {
  const exp = decodeJwt(token).exp;
  return exp ? exp * 1000 : null;
}

const storedToken = loadToken();

export const useAuthStore = create<AuthState>((set) => ({
  token: storedToken,
  sessionId: storedToken ? decodeSessionId(storedToken) : null,
  tokenExpiresAt: storedToken ? decodeExpiresAt(storedToken) : null,
  user: null,
  company: null,
  login: (token, user, company) => {
    // Admin-Tokens landen nur im sessionStorage: F5/Cmd+R im selben Tab überlebt,
    // Tab schließen beendet die Anmeldung. Der 30-Min-Inaktivitäts-Logout und die
    // serverseitige Sitzungs-Whitelist (Admin-Panel → Sitzungen) bleiben bestehen.
    try {
      if (user.role === 'admin') {
        sessionStorage.setItem('eves_token', token);
        localStorage.removeItem('eves_token');
      } else {
        localStorage.setItem('eves_token', token);
        sessionStorage.removeItem('eves_token');
      }
    } catch { /* ignore */ }
    set({ token, sessionId: decodeSessionId(token), tokenExpiresAt: decodeExpiresAt(token), user, company });
  },
  logout: () => {
    try {
      localStorage.removeItem('eves_token');
      sessionStorage.removeItem('eves_token');
    } catch { /* ignore */ }
    set({ token: null, sessionId: null, tokenExpiresAt: null, user: null, company: null });
  },
}));
